import React from 'react';

/**
 * Periodic Table Toggle - Matches periodic table tile style
 * This is the ONLY toggle component - use this everywhere
 * Styled as periodic table tiles when enabled
 */
const Toggle = ({
  enabled,
  onChange,
  disabled = false,
  size = 'default' // 'small' | 'default' | 'large'
}) => {
  // Periodic table tile sizes
  const sizes = {
    small: { width: '28px', height: '28px' },
    default: { width: '39px', height: '39px' },
    large: { width: '48px', height: '48px' }
  };

  const sizeConfig = sizes[size];
  const tileSize = parseInt(sizeConfig.width);

  const handleClick = () => {
    if (!disabled && onChange) {
      onChange(!enabled);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <style>{`
        .periodic-toggle-${size} {
          width: ${sizeConfig.width} !important;
          height: ${sizeConfig.height} !important;
          min-width: ${sizeConfig.width} !important;
          min-height: ${sizeConfig.height} !important;
          padding: 0 !important;
          border-radius: ${tileSize === 28 ? '2.5px' : tileSize === 39 ? '3px' : '3.5px'} !important;
          border: 0.5px solid rgba(255, 255, 255, 0.15) !important;
          position: relative !important;
          overflow: hidden !important;
          cursor: ${disabled ? 'not-allowed' : 'pointer'} !important;
          transition: all 0.2s ease !important;
          opacity: ${disabled ? 0.5 : 1} !important;
          outline: none !important;
          box-shadow: ${enabled
            ? '1px 2px 4px rgba(0, 0, 0, 0.3), 0 0 0 0.5px rgba(0, 0, 0, 0.1)'
            : 'inset 0 1px 3px rgba(0, 0, 0, 0.3)'} !important;
          -webkit-tap-highlight-color: transparent !important;
        }
        
        .periodic-toggle-${size}::before {
          content: '' !important;
          position: absolute !important;
          top: 1px !important;
          left: 1px !important;
          right: 1px !important;
          height: ${tileSize === 28 ? '10px' : tileSize === 39 ? '14px' : '16px'} !important;
          border-radius: ${tileSize === 28 ? '1.5px 1.5px 0 0' : tileSize === 39 ? '2px 2px 0 0' : '2.5px 2.5px 0 0'} !important;
          background: ${enabled
            ? 'linear-gradient(to bottom, rgba(129, 199, 132, 0.7) 0%, rgba(102, 187, 106, 0.3) 40%, transparent 100%)'
            : 'linear-gradient(to bottom, rgba(239, 68, 68, 0.7) 0%, rgba(220, 38, 38, 0.3) 40%, transparent 100%)'} !important;
          pointer-events: none !important;
          z-index: 1 !important;
        }
        
        .periodic-toggle-${size}::after {
          content: '' !important;
          position: absolute !important;
          top: 1px !important;
          left: 1px !important;
          width: ${tileSize === 28 ? '10px' : tileSize === 39 ? '14px' : '16px'} !important;
          bottom: 1px !important;
          border-radius: ${tileSize === 28 ? '1.5px 0 0 1.5px' : tileSize === 39 ? '2px 0 0 2px' : '2.5px 0 0 2.5px'} !important;
          background: ${enabled
            ? 'linear-gradient(to right, rgba(129, 199, 132, 0.6) 0%, rgba(102, 187, 106, 0.2) 40%, transparent 100%)'
            : 'linear-gradient(to right, rgba(239, 68, 68, 0.6) 0%, rgba(220, 38, 38, 0.2) 40%, transparent 100%)'} !important;
          pointer-events: none !important;
          z-index: 1 !important;
        }
        
        .periodic-toggle-wrapper-${size}::before {
          content: '' !important;
          position: absolute !important;
          bottom: 1px !important;
          left: 1px !important;
          right: 1px !important;
          height: ${tileSize === 28 ? '7px' : tileSize === 39 ? '10px' : '12px'} !important;
          border-radius: ${tileSize === 28 ? '0 0 1.5px 1.5px' : tileSize === 39 ? '0 0 2px 2px' : '0 0 2.5px 2.5px'} !important;
          background: ${enabled
            ? 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.1) 60%, rgba(0, 0, 0, 0.25) 100%)'
            : 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.15) 60%, rgba(0, 0, 0, 0.3) 100%)'} !important;
          pointer-events: none !important;
          z-index: 2 !important;
        }
        
        .periodic-toggle-wrapper-${size}::after {
          content: '' !important;
          position: absolute !important;
          top: 1px !important;
          right: 1px !important;
          width: ${tileSize === 28 ? '7px' : tileSize === 39 ? '10px' : '12px'} !important;
          bottom: 1px !important;
          border-radius: ${tileSize === 28 ? '0 1.5px 1.5px 0' : tileSize === 39 ? '0 2px 2px 0' : '0 2.5px 2.5px 0'} !important;
          background: ${enabled
            ? 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.08) 60%, rgba(0, 0, 0, 0.2) 100%)'
            : 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.12) 60%, rgba(0, 0, 0, 0.25) 100%)'} !important;
          pointer-events: none !important;
          z-index: 2 !important;
        }
      `}</style>
      <div className={`periodic-toggle-wrapper-${size}`} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={handleClick}
          disabled={disabled}
          className={`periodic-toggle-${size}`}
          style={{
            background: enabled
              ? 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 30%, #1E7B34 70%, #145A25 100%)'
              : 'linear-gradient(135deg, #DC2626 0%, #B91C1C 30%, #991B1B 70%, #7F1D1D 100%)',
          }}
        >
          {/* Periodic table element - Oxygen (O, 8) when enabled, Nitrogen (N, 7) when disabled */}
          <>
            {/* Atomic number - top right */}
            <span style={{
              position: 'absolute',
              top: tileSize === 28 ? '2px' : tileSize === 39 ? '3px' : '4px',
              right: tileSize === 28 ? '3px' : tileSize === 39 ? '4px' : '5px',
              fontSize: tileSize === 28 ? '6.5px' : tileSize === 39 ? '7px' : '8px',
              fontWeight: 600,
              color: 'white',
              opacity: 0.95,
              zIndex: 15,
              pointerEvents: 'none',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              lineHeight: 1,
            }}>
              {enabled ? '8' : '7'}
            </span>
            {/* Element symbol - centered */}
            <span style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              marginTop: tileSize === 28 ? '1px' : tileSize === 39 ? '-2px' : '2px',
              fontSize: tileSize === 28 ? '16px' : tileSize === 39 ? '18px' : '26px',
              fontWeight: 700,
              color: 'white',
              zIndex: 15,
              pointerEvents: 'none',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              lineHeight: 1,
              letterSpacing: '0',
            }}>
              {enabled ? 'O' : 'N'}
            </span>
          </>
        </button>
      </div>
    </div>
  );
};

export default Toggle;
